import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageSlot } from "@/components/ImageSlot";
import { BoatCard } from "@/components/public/BoatCard";
import { LeadDialog } from "@/components/public/LeadDialog";
import { Seo } from "@/components/Seo";
import { api, fileUrl } from "@/lib/api";

export default function BrandPage() {
  const { key } = useParams();
  const [brand, setBrand] = useState(null);
  const [boats, setBoats] = useState([]);
  const [award, setAward] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBrand(null); setNotFound(false);
    api.get(`/brands/${key}`).then((r) => {
      setBrand(r.data);
      api.get(`/boats?brand=${encodeURIComponent(r.data.name)}`).then((b) => setBoats((b.data || []).filter((x) => x.status !== "sold"))).catch(() => {});
    }).catch(() => setNotFound(true));
    api.get(`/awards?placement=brand&brand_key=${key}`).then((r) => setAward((r.data || [])[0] || null)).catch(() => {});
  }, [key]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="text-3xl font-bold font-heading">Brand not found</h1><Button asChild className="mt-6 bg-mtm-purple text-white rounded-full"><Link to="/brands">All Brands</Link></Button></div>;
  if (!brand) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid={`brand-page-${key}`}>
      <Seo title={brand.meta_title || `${brand.name} | MTM Marine`} description={brand.meta_description || brand.short_description} />
      <section className="relative bg-gradient-to-br from-mtm-purple-deep via-mtm-purple to-mtm-teal-dark text-white overflow-hidden">
        {brand.hero_image_path && (
          <>
            <img src={fileUrl(brand.hero_image_path, 2000)} alt={`${brand.name} boat`} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-mtm-purple-deep/95 via-mtm-purple-deep/75 to-mtm-purple-deep/30" />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          {brand.logo_path && (
            <span className="bg-white rounded-xl p-3 inline-block mb-5 shadow-sm">
              <img src={fileUrl(brand.logo_path, 440)} alt={`${brand.name} logo`} className="h-16 w-auto object-contain block" />
            </span>
          )}
          <p className="text-xs font-bold uppercase tracking-widest text-mtm-teal-bright font-mono">MTM Marine · Bristol, TN</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight mt-3">{brand.name}</h1>
          <p className="text-lg text-slate-200 mt-4 max-w-2xl">{brand.short_description}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">About {brand.name}</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{brand.story}</p>
          <div className="flex gap-3 mt-6">
            <Button asChild className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full"><Link to={`/inventory?brand=${encodeURIComponent(brand.name)}`}>View {brand.name} Inventory</Link></Button>
            <LeadDialog ctaType={`${brand.name} Inquiry`} triggerLabel="Request Info" variant="teal" />
          </div>
        </div>
        <ImageSlot label={`MTM-${brand.name.replace(/\s+/g, "-")}-Feature.jpg`} dimensions="900 × 675" />
      </section>

      {award && (
        <section className="max-w-7xl mx-auto px-4 pb-6">
          <div className="bg-gradient-to-r from-mtm-purple-deep to-mtm-purple text-white rounded-2xl p-8 flex items-center gap-6" data-testid="brand-award">
            <Award size={48} className="text-mtm-teal-bright shrink-0" />
            <div><h3 className="font-heading font-bold text-xl">{award.title}</h3><p className="text-slate-200 text-sm mt-1">{award.description}</p></div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">{brand.name} Inventory</h2>
          <Link to={`/inventory?brand=${encodeURIComponent(brand.name)}`} className="hidden sm:flex items-center gap-1 text-sm font-semibold text-mtm-purple hover:gap-2 transition-all">View All <ArrowRight size={15} /></Link>
        </div>
        {boats.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{boats.slice(0, 6).map((b, i) => <BoatCard key={b.id} boat={b} index={i} />)}</div>
        ) : (
          <p className="text-slate-500">No {brand.name} boats in stock right now. <LeadDialog ctaType={`${brand.name} Inquiry`} triggerLabel="Ask about availability" variant="outline" className="ml-2" /></p>
        )}
      </section>
    </div>
  );
}
